import { ChevronDownIcon } from "@heroicons/react/24/outline";
import React from "react";
import { useState } from "react";

import { SEOHelmet } from "../components/common/SEOHelmet";
import { calculateYears } from "../utils/utils";
import { PATH_ABOUT } from "../utils/constants";

// Types
interface Training {
  title: string;
  location?: string;
  date: string;
}

interface TrainingCategory {
  categoryName?: string;
  trainings: Training[];
}

interface Highlight {
  title: string;
  location?: string;
  date?: string;
  ratio?: string;
  image?: string;
  href?: string;
}

interface TeamMemberPhotoProps {
  src: string;
  name?: string;
  brightness?: string;
  className?: string;
}

interface TeamMemberSectionProps {
  name: string;
  imageSrc: string;
  content: string;
  trainings: TrainingCategory[];
  brightness?: string;
  role?: string;
}

const highlights: Highlight[] = [
  {
    title:
      "Autorin der Broschüre: Liebe, Lust und Leidenschaft, Sexualität nach Schlaganfall (für die Stiftung der Deutschen Schlaganfall-Hilfe)",
    location: "Gütersloh",
    date: "2020",
    ratio: "aspect-[4/5]",
    image: process.env.PUBLIC_URL + "/Sexualität_nach_Schlaganfall_Titel.jpg",
  },
  // {
  //   title:
  //     "Ausbildung zur Sexualberaterin in klinischer Sexologie nach Sexocorporel",
  //   location: "Kißlegg",
  //   date: "2017 - 2019",
  //   ratio: "aspect-[4/5]",
  // },
];

const trainingsMeyer: TrainingCategory[] = [
  {
    categoryName: "Neurologie",
    trainings: [
      {
        title: "Pacing 2.0 - Aufbauseminar",
        location: "online",
        date: "18.09-19.09.2026",
      },
      {
        title: "HoDT Grundkurs",
        location: "Elzach",
        date: "09.10-11.10.2025, 27.11-29.11.2025",
      },
      {
        title:
          "PACING 2.0 - Ressourcenorientiertes Therapieprogramm für mehr Wohlbefinden & Teilhabe bei Post COVID & ME/CFS",
        location: "online",
        date: "14.11-15.11.2025",
      },
      {
        title: "Hemiparese-Behandlung der OEX",
        location: "online",
        date: "23.06-25.06.2025",
      },
      {
        title: "Notfalltraining für Ersthelfende, speziell für unsere Praxis",
        location: "Rottweil",
        date: "26.04.2025",
      },
      {
        title: "TheraPro, Fachmesse für Therapie, Prävention und Rehabilitation",
        location: "Stuttgart",
        date: "02.02.2025",
      },
      {
        title: "Energiemanagement-Schulung für Menschen mit Fatigue (EMS)",
        location: "Valens",
        date: "",
      },
      {
        title: "Post Covid im ambulanten Setting",
        date: "02.11.24",
      },
      {
        title:
          "Rottweiler Herbstsymposium für Psychiatrie, Psychotherapie und Psychosomatik im Vinzenz von Paul Hospital",
        location: "Rottweil",
        date: "09.11.14",
      },
      {
        title: "Neurotraining nach Verena Schweizer",
        location: "Zürich",
        date: "18.11/19.11.24",
      },
      {
        title: "Hilfsmittelversorgung",
        location: "Albstadt",
        date: "05.12.24",
      },
    ],
  },
  {
    categoryName: "Psychiatrie",
    trainings: [
      {
        title:
          "Metakognitives Training und MKT + für Patient:innen mit Psychose",
        location: "online",
        date: "17.04-18.04.2026",
      },
      {
        title: "IPT Kompaktkurs",
        location: "Freiburg",
        date: "20.06-22.06.24",
      },
      {
        title:
          "Rottweiler Herbstsymposium für Psychiatrie, Psychotherapie und Psychosomatik im Vinzenz von Paul Hospital",
        location: "Rottweil",
        date: "09.11.14",
      },
    ],
  },
];

const trainingsFroehlich: TrainingCategory[] = [
  {
    categoryName: "Handtherapie",
    trainings: [
      {
        title:
          "HT10-Anatomie, Biomechanik und Pathophysiologie des Schultergelenks und des Schultergürtels",
        location: "Fellbach",
        date: "19.02-20.02.2026",
      },
      {
        title:
          "HT9-Funktionelle Zusammenhänge zwischen Hand, Unterarm und Ellenbogen",
        location: "Fellbach",
        date: "17.02-18.02.2026",
      },
      {
        title: "HT6-Basiskurs Hand V \"Hand, Tape, Cast und Fertigorthesen\"",
        location: "Fellbach",
        date: "02.08-03.08.2025",
      },
      {
        title: "HT5-Basiskurs Hand IV \"Hand und Narbenbehandlung\"",
        location: "Fellbach",
        date: "31.07-01.08.2025",
      },
      {
        title: "HT4-Basiskurs Hand III \"Hand und Weichteiltechniken\"",
        location: "Fellbach",
        date: "29.07-30.07.2025",
      },
      {
        title: "HT3-Basiskurs Hand II \"Hand- und Radioulnargelenk\"",
        location: "Fellbach",
        date: "04.06-05.06.2025",
      },
      {
        title: "HT2-Basiskurs Hand I „Finger und Daumengelenke“",
        location: "Fellbach",
        date: "02.06-03.06.2025",
      },
      {
        title: "HT1-Einführungskurs Hand: „Anatomie der Hand“",
        location: "Fellbach",
        date: "31.05-01.06.2025",
      },
      {
        title: "Evidenzbasierte Hand- und Armbehandlung in der Neurologie",
        date: "15.07.24",
      },
      {
        title: "CRPS (Mb.Sudeck) ganzheitlich behandeln",
        date: "10.07.24",
      },
      {
        title: "Narbentherapie",
        date: "Juli 2024",
      },
    ],
  },
  {
    categoryName: "Pädiatrie",
    trainings: [
      {
        title: "Basiswissen Autismus und Grundlagen des TEACCH-Ansatzes",
        date: "15.02.2025",
      },
      {
        title: "Spielideen zur Förderung von Körperbewusstsein und Wahrnehmung",
        date: "10.04.24",
      },
      {
        title: "Den Selbstwert von Kindern und Jugendlichen stärken",
        date: "11.04.24",
      },
    ],
  },
  {
    categoryName: "Neurologie",
    trainings: [
      {
        title: "HoDT Grundkurs",
        location: "Elzach",
        date: "09.10-11.10.2025, 27.11-29.11.2025",
      },
      {
        title: "Notfalltraining für Ersthelfende, speziell für unsere Praxis",
        location: "Rottweil",
        date: "26.04.2025",
      },
      {
        title: "TheraPro, Fachmesse für Therapie, Prävention und Rehabilitation",
        location: "Stuttgart",
        date: "02.02.2025",
      },
    ],
  },
];

const trainingsDeutschmann: TrainingCategory[] = [
  {
    categoryName: "Pädiatrie",
    trainings: [
      {
        title: "Aufbaustudium zur diplomierten Lerndidaktikerin",
        location: "Erster Österreichischer Dachverband Legasthenie",
        date: "01.04.2026 bis heute",
      },
      {
        title: "Erste Hilfe am Kind",
        location: "Wilflingen",
        date: "17.11/24.11.2025",
      },
      {
        title: "Grundkurs: Geschickte Hände",
        date: "16.10-18.10.2025",
      },
      {
        title: "NLP für Kinder",
        location: "Akademie für Kinder und Jugendcoaching",
        date: "13.09-14.09.2025",
      },
      {
        title: "Basiswissen Autismus und Grundlagen des TEACCH-Ansatzes",
        date: "2025",
      },
      {
        title: "Klinische Vorschultrainerin nach TFT:Fit für die Schule 1-4 (80UE)",
        location: "Landau",
        date: "2018",
      },
      {
        title: "Diplomierte Legasthenietrainerin",
        location: "durch den ersten österreichischen Dachverband Legasthenie",
        date: "2017",
      },
    ],
  },
  {
    categoryName: "Neurologie",
    trainings: [
      {
        title: "Cranio 1 - Basiskurs",
        location: "Tübingen",
        date: "08.05-10.05.2026",
      },
      {
        title: "Notfalltraining für Ersthelfende, speziell für unsere Praxis",
        location: "Rottweil",
        date: "26.04.2025",
      },
      {
        title: "TheraPro, Fachmesse für Therapie, Prävention und Rehabilitation",
        location: "Stuttgart",
        date: "2025",
      },
      {
        title: "Funktionstraining an der hemiplegischen Hand",
        location: "Tübingen",
        date: "2013",
      },
      {
        title: "Analyse und Funktionen der OEX",
        location: "Kassel",
        date: "2012",
      },
    ],
  },
];

const trainingsUtech: TrainingCategory[] = [];

const trainingsNagel: TrainingCategory[] = [
  {
    categoryName: "Pädiatrie",
    trainings: [
      {
        title: "AD(H)S-Trainer (sechsteilige Serie)",
        location: "online",
        date: "21.08-22.08.2026, 25.09-26.09.2026, 06.11-07.11.2026",
      },
      {
        title:
          "Lernberatung als Baustein in der Ergotherapie - Kinder und Eltern befähigen, Hausaufgaben und Lernen zu Hause zu bewältigen",
        location: "online",
        date: "16.04-17.04.2026",
      },
      {
        title:
          "Basiswissen - Autismus und Grundlagen des TEACCH-Ansatzes",
        location: "online",
        date: "13.02.2026",
      },
      {
        title: "Erste Hilfe am Kind",
        location: "Wilflingen",
        date: "17.11/24.11.2025",
      },
      {
        title: "SKIP KJG Kurspaket Jugendleiter-kompetent Gruppen leiten",
        location: "Tuttlingen",
        date: "2021",
      },
      {
        title: "Ausbildung Schulsportmentor Sekundarstufe 1",
        location: "Tuttlingen",
        date: "2016/2017",
      },
    ],
  },
];

const trainings: TrainingCategory[] = [
  {
    categoryName: "Neurologie/Geriatrie",
    trainings: [
      {
        title: "Notfalltraining für Ersthelfende, speziell für unsere Praxis",
        location: "Rottweil",
        date: "26.04.2025",
      },
      {
        title: "TheraPro, Fachmesse für Therapie, Prävention und Rehabilitation",
        location: "Stuttgart",
        date: "02.02.2025",
      },
      {
        title: "Energiemanagement-Schulung für Menschen mit Fatigue (EMS)",
        location: "Karlsruhe",
        date: "05.07-06.07.2024",
      },
      {
        title: "Hospizliche Begleitung und palliative Pflege sterbenskranker Menschen",
        location: "Rottweil",
        date: "22.02.2017",
      },
      {
        title: "Pflege nach Bobath-Lagern und positionieren im Bett",
        location: "Rottweil",
        date: "15.03.2016",
      },
      {
        title: "Einführung in die F.O.T.T",
        location: "Burgau",
        date: "03.03-04.03.2016",
      },
      {
        title: "Special Edition Neurologie",
        location: "Bochum",
        date: "06.-07.11.2015",
      },
      {
        title: "LIN- Lagerung in Neutralnullstellung",
        location: "Burgau",
        date: "16.-18.10.2015",
      },
      {
        title: "Befundung und Behandlung von Hemiplegiepatienten mit schmerzhafter Schulter",
        location: "Reutlingen",
        date: "17.07-18.07.2015",
      },
      {
        title:
          "Rottweiler Herbstsymposium für Psychiatrie, Psychotherapie und Psychosomatik im Vinzenz von Paul Hospital",
        location: "Rottweil",
        date: "09.11.2014",
      },
      { title: "Gelenksymposium", location: "Leipzig", date: "08.03.2014" },
      {
        title: "Teilhaben- (neuro)logisch!",
        location: "Stuttgart",
        date: "26.01.2014",
      },
      {
        title: "Hemiparese: Behandlung der oberen Extremität",
        location: "Reutlingen",
        date: "30.11 - 02.12. 2013",
      },
      {
        title: "Bobath- 24- Stunden-Konzept",
        location: "Reutlingen",
        date: "28.11-30.11.2013",
      },
      {
        title: "Bobath Workshop Multiple Sklerose",
        location: "Kassel",
        date: "11.11-15.11.2013",
      },
      {
        title: "HoDT (Handlungsorientierte Diagnostik und Therapie)",
        location: "Berlin",
        date: "13.-19.10.2014",
      },
      {
        title: "Aufgabenorientiertes Training: posturale Kontrolle",
        location: "Allensbach",
        date: "19.10.2013",
      },
    ],
  },
  {
    categoryName: "Psychiatrie",
    trainings: [
      {
        title:
          "Auf geht's! Affektregulierung mit Körperinterventionen. Die Body2brain Methode",
        location: "Tübingen",
        date: "21.03.2026",
      },
      {
        title: "„Curriculum zur Interpersonellen Psychotherapie (IPT) - Kompaktkurs: Einführung und Aufbau“",
        location: "Freiburg",
        date: "27.04-29.04.2017",
      },
      {
        title:
          "Psychotherapie Fernlehrgang zur Vorbereitung auf die amtsärztliche Prüfung nach dem Heilpraktikergesetz",
        location: "Institut für Lernsysteme",
        date: "14.03.2016-14.06.2017",
      },
      {
        title: "Ergotherapie von Menschen mit Schizophrenie und wahnhaften Störungen",
        location: "Marburg",
        date: "12.-13.12.2014",
      },
      {
        title: "25.Rottweiler Herbstsymposium",
        location: "Rottweil",
        date: "12.11.2016",
      },
      { title: "Delir", location: "Rottweil", date: "12.10.2016" },
      {
        title: "Deeskalationstraining",
        location: "Rottweil",
        date: "14./15./28./29.09.2015",
      },
    ],
  },
  {
    categoryName: "Pädiatrie",
    trainings: [
      {
        title: "Basiswissen Autismus",
        date: "19.10.2024",
      },
      {
        title: "Spielideen zur Förderung von Körperbewusstsein und Wahrnehmung",
        date: "10.04.2024",
      },
      {
        title: "Kindernotfalltraining",
        location: "Steinenbronn",
        date: "06.09.2019",
      },
      {
        title: "TLS in der therapeutischen Praxis",
        location: "Bellheim",
        date: "11.06-15.06.2014",
      },
      {
        title: "Präventive Ergotherapie an Grundschulen",
        location: "Mannheim",
        date: "14.09.2013",
      },
    ],
  },
  {
    categoryName: "Sexualität",
    trainings: [
      {
        title:
          "Die Kraft der Lust – Systemische Sexualtherapie für eine lebendige Sexualität",
        location: "Tübingen",
        date: "20.03.2026",
      },
      {
        title: "Zwei jährige Weiterbildung: Sexualtherapie in klinischer Sexologie nach Sexocorporel",
        location: "Kißlegg",
        date: "2017- 2019",
      },
      {
        title: "Multiplikatoren Schulung zum Thema sexualisierte Gewalt in Einrichtungen",
        location: "Untermachtal",
        date: "2022",
      },
    ],
  },
  {
    categoryName: "Zusatzqualifikationen",
    trainings: [
      {
        title: "Migränetherapie nach Kern",
        location: "Freiburg",
        date: "01.10-03.10.2025",
      },
      {
        title: "Betätigungsorientierte Ergotherapie",
        location: "Stuttgart",
        date: "21.03.2015",
      },
      {
        title: "Besprechungen in Teams -Ergebnisorientiert Führen",
        location: "Rottweil",
        date: "20.02.2017",
      },
      {
        title: "Einführung in das „Ergotherapeutische Assessment“",
        location: "Karlsbad-Langensteinbach",
        date: "14.-16.03.2011",
      },
    ],
  },
];

// Components
const TrainingCard: React.FC<Training> = ({ title, location, date }) => (
  <div className="flex items-baseline justify-between gap-4 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200">
    <div className="min-w-0">
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">
        {title}
      </p>
      {location && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{location}</p>
      )}
    </div>
    {date && (
      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">{date}</span>
    )}
  </div>
);

const TrainingCategoryComponent: React.FC<TrainingCategory> = ({ categoryName, trainings }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-5 py-4 text-left group
          hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-200">
            {categoryName}
          </span>
          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/60 rounded-full px-2 py-0.5">
            {trainings.length}
          </span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`transition-all duration-300 ease-in-out ${isExpanded ? "opacity-100 max-h-[2000px]" : "opacity-0 max-h-0 overflow-hidden"}`}
      >
        <div className="px-5 pb-5 space-y-1">
          {trainings.map((training, index) => (
            <TrainingCard key={index} {...training} />
          ))}
        </div>
      </div>
    </div>
  );
};

const HighlightCard: React.FC<Highlight> = ({ title, location, date, ratio, image, href }) => (
  <div
    className="group rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/40 overflow-hidden cursor-pointer
      hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-300"
    onClick={() => href && window.open(href, "_blank")}
  >
    {image && (
      <div className="aspect-[4/3] overflow-hidden">
        <img
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          src={image}
          alt={title}
        />
      </div>
    )}
    <div className="p-4">
      <h3 className="text-sm leading-snug font-medium text-gray-700 dark:text-gray-300 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-300">
        {title}
      </h3>
      <div className="mt-1.5 flex text-xs text-gray-400 dark:text-gray-500">
        {location && <p className="leading-4 after:content-[','] after:mr-1">{location}</p>}
        <p className="leading-4">{date}</p>
      </div>
    </div>
  </div>
);

const TeamMemberPhoto: React.FC<TeamMemberPhotoProps> = ({ src, name, brightness = "", className = "" }) => (
  <div className="group relative">
    <div
      className={`aspect-[3/4] rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800 max-w-[300px] ${className}`}
    >
      <img
        src={process.env.PUBLIC_URL + src}
        alt={name ? `${name} - Ergotherapeutin bei Ergotherapie Rottweil` : "Team der Ergotherapie Rottweil"}
        className={`w-full h-full object-cover ${brightness}`}
      />
    </div>
    {name && (
      <h3 className="mt-4 text-center">
        <span className="text-gray-900 dark:text-white font-playfair italic text-base tracking-wide">{name}</span>
      </h3>
    )}
  </div>
);

const TeamMemberSection: React.FC<TeamMemberSectionProps> = ({
  name,
  imageSrc,
  content,
  trainings,
  brightness = "",
  role,
}) => (
  <div className="mt-14">
    <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-10">
      <div className="min-w-0 max-w-2xl">
        <div className="mb-4">
          <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            {name}
          </h3>
          {role && (
            <p className="mt-1 text-sm font-medium text-ergo-600 dark:text-ergo-400">
              {role}
            </p>
          )}
        </div>
        <div className="space-y-3 text-base text-gray-500 dark:text-gray-400 leading-relaxed">
          {content.split("\n\n").map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </div>
      <img
        src={process.env.PUBLIC_URL + imageSrc}
        alt={`${name} - ${role ?? "Ergotherapeutin, Praxisinhaberin"} bei Ergotherapie Rottweil`}
        className={`mx-auto w-64 shrink-0 md:w-72 lg:mx-0 lg:w-64 xl:w-72 aspect-[3/4] rounded-2xl object-cover shadow-sm ${brightness}`}
      />
    </div>
    {trainings.length > 0 && (
      <div className="mt-10">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 tracking-wide mb-3">
          Fort- & Weiterbildungen
        </p>
        <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-800/40 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/40">
          {trainings.map((category, index) => (
            <TrainingCategoryComponent key={index} {...category} />
          ))}
        </div>
      </div>
    )}
  </div>
);

const PromotionBanner: React.FC<{ href: string }> = ({ href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="block group -mt-6">
    <div
      className="bg-white dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/40 rounded-2xl p-5
      hover:border-ergo-300 dark:hover:border-ergo-700 transition-colors duration-300"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="rounded-xl bg-ergo-50 dark:bg-ergo-900/80 p-3">
            <svg
              className="w-6 h-6 text-ergo-600 dark:text-ergo-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Mehr zu Sexualität auf</p>
            <p className="text-lg font-semibold text-ergo-600 dark:text-ergo-400">sx-med.de</p>
          </div>
        </div>
        <svg
          className="w-5 h-5 text-gray-400 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  </a>
);

export default function AboutPage() {
  return (
    <>
      <SEOHelmet page="about" pathname={PATH_ABOUT} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalOrganization",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "alternateName": "ERGOTHERAPIE UND ZEIT(T)RÄUME",
          "url": "https://ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "founder": {
            "@type": "Person",
            "name": "Lisa Spreitzer",
            "jobTitle": "Ergotherapeutin",
            "description": "Inhaberin und Gründerin der Ergotherapie-Praxis mit Schwerpunkt Neurologie, Geriatrie und Psychiatrie"
          },
          "employee": [
            {
              "@type": "Person",
              "name": "Sandra Meyer",
              "jobTitle": "Ergotherapeutin",
              "description": "Spezialistin für Energiemanagement-Schulung und Neurologie"
            },
            {
              "@type": "Person",
              "name": "Charlotte Fröhlich",
              "jobTitle": "Ergotherapeutin",
              "description": "Spezialistin für Pädiatrie und Psychiatrie, Elterntraining"
            },
            {
              "@type": "Person",
              "name": "Jana Deutschmann",
              "jobTitle": "Ergotherapeutin",
              "description": "Diplomierte Legasthenietrainerin, Spezialistin für Pädiatrie"
            },
            {
              "@type": "Person",
              "name": "Paula Nagel",
              "jobTitle": "Ergotherapeutin",
              "description": "Spezialistin für ressourcenorientierte Therapie und Gruppenbehandlungen"
            },
            {
              "@type": "Person",
              "name": "Susan Maier",
              "jobTitle": "Sozialpädagogin und Sozialarbeiterin",
              "description": "Koordinatorin des Netzverbund Schwarzwald, Schwerpunkt Koordination und Organisation von Behandlungsmöglichkeiten"
            }
          ]
        })}
      </script>

      <article className="space-y-14">
        {/* Team Overview Section */}
        <header>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
            Unser Team
          </h1>
          <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-10">
            Unser Team besteht aus erfahrenen Therapeutinnen, die sich leidenschaftlich für die ganzheitliche
            Betreuung ihrer Klienten einsetzen und durch ihre individuellen Stärken und Fachgebiete eine umfassende
            Versorgung gewährleisten.
          </p>

          {/* Team Photos Grid */}
          <section className="space-y-8">
            <div className="w-full">
              <TeamMemberPhoto src="/images/uns.jpeg" className="!aspect-[16/9] !max-w-full" />
            </div>
          </section>
        </header>

        {/* Individual Team Member Sections */}
        <section className="px-6">
          <h2 className="sr-only">Team Mitglieder Details</h2>

          {/* Lisa Spreitzer Section */}
          <article>
            <TeamMemberSection
              name="Lisa Spreitzer"
              imageSrc="/images/lisa.jpeg"
              content={`Mein Name ist Lisa Spreitzer. Ich bin verheiratet, Mutter von zwei Kindern und arbeite seit ${calculateYears(
                "04/01/2013"
              )} Jahren mit großer Leidenschaft als Ergotherapeutin. Durch meine langjährige Erfahrung in den Bereichen Neurologie, Geriatrie und Psychiatrie ist mir eine alltagsnahe und praxisorientierte Therapie besonders wichtig. Gemeinsam entwickeln wir individuelle Strategien, um Ihren Alltag erfolgreich zu meistern und Kinder sowie Jugendliche frühzeitig in ihrer Entwicklung zu unterstützen.`}
              trainings={trainings}
              brightness="brightness-125"
            />
          </article>

          {/* Highlights Section */}
          <section className="space-y-6">
            <h3 className="sr-only">Highlights und Publikationen</h3>
            <div className="mx-auto">
              <PromotionBanner href="https://www.sx-med.de" />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((highlight, index) => (
                <HighlightCard key={index} {...highlight} />
              ))}
            </div>
          </section>

          {/* Sandra Meyer Section */}
          <article>
            <TeamMemberSection
              name="Sandra Meyer"
              imageSrc="/images/sandra_meyer.jpeg"
              content={"Als Ergotherapeutin arbeite ich nach aktuellen wissenschaftlichen Erkenntnissen und lege besonderen Wert auf eine ganzheitliche, alltagsnahe Therapie. Während eines Praktikums in den Kliniken Valens (Schweiz) lernte ich die positiven Effekte des Energiemanagements kennen. Daher biete ich in unserer Praxis Schulungen zum Energiemanagement in Kleingruppen mit maximal fünf Teilnehmenden an. Das Angebot richtet sich insbesondere an Menschen mit anhaltender Müdigkeit, Erschöpfung und verminderter Belastbarkeit, um ihre Lebensqualität nachhaltig zu verbessern."}
              trainings={trainingsMeyer}
            />
          </article>

          {/* Charlotte Fröhlich Section */}
          <article>
            <TeamMemberSection
              name="Charlotte Fröhlich"
              imageSrc="/images/charlotte_froehlich.jpeg"
              content={"Die ganzheitlichen Möglichkeiten der Ergotherapie begeistern mich. Meine Schwerpunkte liegen in den Bereichen Psychiatrie und Pädiatrie. Ich leite Elterngruppen und unterstütze Familien mit Transparenz, Anleitung und praxisnahen Strategien. Ein besonderer Fokus meiner Arbeit liegt auf dem Elterntraining für Kinder mit hyperkinetischem und oppositionellem Verhalten."}
              trainings={trainingsFroehlich}
            />
          </article>

          {/* Jana Deutschmann Section */}
          <article>
            <TeamMemberSection
              name="Jana Deutschmann"
              imageSrc="/images/jana_deutschmann.jpeg"
              content={"Meine große Leidenschaft ist die Pädiatrie und die Arbeit mit Kindern. Dabei ist mir die enge Zusammenarbeit mit Eltern, Lehrkräften und weiteren Bezugspersonen besonders wichtig. In der Therapie orientiere ich mich an den Stärken und Interessen des Kindes, um positive Lernerfahrungen zu schaffen und Entwicklungsbereiche gezielt zu fördern. Als diplomierte Legasthenietrainerin begleite ich zudem Kinder und Familien im Bereich Lese-Rechtschreib-Schwäche (LRS)."}
              trainings={trainingsDeutschmann}
            />
          </article>

          {/* Paula Nagel Section */}
          <article>
            <TeamMemberSection
              name="Paula Nagel"
              imageSrc="/images/paula_nagel.jpeg"
              content={"Es ist mir ein großes Anliegen, Ressourcen orientiert zu arbeiten und am Alltag mit unseren Klienten:Innen anzusetzen. Dabei liegt der Fokus auf den vorhandenen Fähigkeiten und Stärken jedes Einzelnen.\n\nIch arbeite sehr gerne mit Erwachsenen und Kindern. Eine große Chance sehe ich in Gruppenbehandlungen. Hier können viele Fähigkeiten geübt werden: soziale Interaktion, Teamfähigkeit, Kommunikation, Kooperation und Hilfsbereitschaft, Absprachen, Perspektivwechsel etc."}
              trainings={trainingsNagel}
            />
          </article>

          {/* Susan Maier Section */}
          <article>
            <TeamMemberSection
              name="Susan Maier"
              role="Sozialpädagogin und Sozialarbeiterin · Koordinatorin des Netzverbund Schwarzwald"
              imageSrc="/images/susan_maier.jpeg"
              content={"Für mich steht der Mensch als Individuum im Mittelpunkt. Daher ist es mir sehr wichtig, Menschen mit komplexen psychischen Erkrankungen individuell zu beraten und sie auf ihrem Behandlungsweg zu unterstützen.\n\nIch bilde das Bindeglied zwischen Klient:in und den verschiedenen beteiligten Fachdisziplinen und kümmere mich darum, dass aus einzelnen Bausteinen eine stimmige und tragfähige Gesamtbehandlung entstehen kann. Mein Schwerpunkt liegt dabei in der Koordination und Organisation von Behandlungsmöglichkeiten, um Sie möglichst ganzheitlich betreuen zu können.\n\nMit Empathie, Verlässlichkeit und einem offenen Ohr unterstütze ich Sie dabei, Stabilität im Alltag zu finden und Sie über passende Hilfen zu informieren, um Ihre Versorgung möglichst umfassend zu verbessern."}
              trainings={[]}
            />
          </article>

          {/* Petra Utech Section */}
          <article>
            <TeamMemberSection
              name="Petra Utech"
              imageSrc="/images/petra_utech.jpeg"
              content={"Ich sorge dafür, dass in unserer Ergotherapiepraxis alles reibungslos läuft. Mit einem offenen Ohr für Ihre Anliegen und einem geschulten Blick für Organisation und Planung bin ich die Ansprechpartnerin für Terminvergaben, administrative Abläufe und alles, was hinter den Kulissen wichtig ist.\n\nEs ist mir wichtig, dass Sie sich bei uns rundum wohlfühlen."}
              trainings={trainingsUtech}
            />
          </article>
        </section>
      </article>
    </>
  );
}
